import React from "react";
import { createScopedLogger } from "../../common";
import { IComponent } from "../Models/IComponent";
import ButtonComponent from "./Button/ButtonComponent";
import ButtonGroupComponent from "./ButtonGroup/ButtonGroupComponent";
import ChipComponent from "./Chip/ChipComponent";
import ValueCounterComponent from "./Dashboards/ValueCounterComponent";
import DataDisplayComponent from "./DataDisplay/DataDisplayComponent";
import DrawerMenuComponent from "./DrawerMenu/DrawerMenuComponent";
import GlobalSearchComponent from "./GlobalSearch/GlobalSearchComponent";
import GridContainerComponent from "./GridContainer/GridContainerComponent";
import IComponentProps from "./IComponentProps";
import ImageComponent from "./Image/ImageComponent";
import InputCheckboxComponent from "./InputCheckbox/InputCheckboxComponent";
import InputFieldComponent from "./InputField/InputFieldComponent";
import InputFileComponent from "./InputFile/InputFileComponent";
import InputSelectComponent from "./InputSelect/InputSelectComponent";
import InputSliderComponent from "./InputSlider/InputSliderComponent";
import LinkComponent from "./Link/LinkComponent";
import ListComponent from "./List/ListComponent";
import ListSelectComponent from "./List/ListSelectComponent";
import ListMessageChatComponent from "./ListMessageChat/ListMessageChatComponent";
import ListWithSideDetailComponent from "./ListWithSideDetail/ListWithSideDetailComponent";
import PanelComponent from "./Panel/PanelComponent";
import PopoverContainerComponent from "./PopoverContainer/PopoverContainerComponent";
import ReactComponentHost from "./ReactComponentHost/ReactComponentHost";
import ReferenceComponent from "./Reference/ReferenceComponent";
import RepeatContainerComponent from "./RepeatContainer/RepeatContainerComponent";
import StackContainerComponent from "./StackContainer/StackContainerComponent";
import TabsComponent from "./Tabs/TabsComponent";
import TimelineComponent from "./Timeline/TimelineComponent";
import TypographyComponent from "./Typography/TypographyComponent";
import UnknownComponent from "./Unknown/UnknownComponent";
import ViewComponent from "./View/ViewComponent";
import ViewBreadcrumbsComponent from "./ViewBreadcrumbs/ViewBreadcrumbsComponent";
import WebViewComponent from "./WebView/WebViewComponent";

const ChatComponent = React.lazy(() => import("./Chat/ChatComponent"));
const VoiceAgentComponent = React.lazy(() => import("./VoiceAgent/VoiceAgentComponent"));
const BarChartComponent = React.lazy(() => import("./Dashboards/BarChartComponent"));
const PieChartComponent = React.lazy(() => import("./Dashboards/PieChartComponent"));
const FileTableComponent = React.lazy(() => import("./FileTable/FileTableComponent"));
const MapDisplayComponent = React.lazy(() => import("./MapDisplay/MapDisplayComponent"));
const MarkdownDisplayComponent = React.lazy(() => import("./MarkdownDisplay/MarkdownDisplayComponent"));
const TableComponent = React.lazy(() => import("./Table/TableComponent"));

type ComponentFactoryProps = IComponentProps<IComponent>;
const logger = createScopedLogger("ComponentFactory");

const ComponentFactory: React.FC<ComponentFactoryProps> = ({ parent, component, context, componentPath = [] }) => {
  const typedComponent = component as any;
  const key = `${component.Id}-${context.StableId}`;

  switch (component.Type) {
    case "Typography":
      return <TypographyComponent key={key} parent={parent} component={typedComponent} context={context} componentPath={componentPath} />;
    case "Button":
      return <ButtonComponent key={key} parent={parent} component={typedComponent} context={context} componentPath={componentPath} />;
    case "ButtonGroup":
      return <ButtonGroupComponent key={key} parent={parent} component={typedComponent} context={context} componentPath={componentPath} />;
    case "Chat":
      return <ChatComponent key={key} parent={parent} component={typedComponent} context={context} componentPath={componentPath} />;
    case "VoiceAgent":
      return <VoiceAgentComponent key={key} parent={parent} component={typedComponent} context={context} componentPath={componentPath} />;
    case "DataDisplay":
      return <DataDisplayComponent key={key} parent={parent} component={typedComponent} context={context} componentPath={componentPath} />;
    case "DrawerMenu":
      return <DrawerMenuComponent key={key} parent={parent} component={typedComponent} context={context} componentPath={componentPath} />;
    case "FileTable":
      return <FileTableComponent key={key} parent={parent} component={typedComponent} context={context} componentPath={componentPath} />;
    case "List":
      return <ListComponent key={key} parent={parent} component={typedComponent} context={context} componentPath={componentPath} />;
    case "ListSelect":
      return <ListSelectComponent key={key} parent={parent} component={typedComponent} context={context} componentPath={componentPath} />;
    case "ListMessageChat":
      return <ListMessageChatComponent key={key} parent={parent} component={typedComponent} context={context} componentPath={componentPath} />;
    case "ListWithSideDetail":
      return <ListWithSideDetailComponent key={key} parent={parent} component={typedComponent} context={context} componentPath={componentPath} />;
    case "Link":
      return <LinkComponent key={key} parent={parent} component={typedComponent} context={context} componentPath={componentPath} />;
    case "MarkdownDisplay":
      return <MarkdownDisplayComponent key={key} parent={parent} component={typedComponent} context={context} componentPath={componentPath} />;
    case "MapDisplay":
      return <MapDisplayComponent key={key} parent={parent} component={typedComponent} context={context} componentPath={componentPath} />;
    case "Table":
      return <TableComponent key={key} parent={parent} component={typedComponent} context={context} componentPath={componentPath} />;
    case "GridContainer":
      return <GridContainerComponent key={key} parent={parent} component={typedComponent} context={context} componentPath={componentPath} />;
    case "GlobalSearch":
      return <GlobalSearchComponent key={key} parent={parent} component={typedComponent} context={context} componentPath={componentPath} />;
    case "Image":
      return <ImageComponent key={key} parent={parent} component={typedComponent} context={context} componentPath={componentPath} />;
    case "InputCheckbox":
      return <InputCheckboxComponent key={key} parent={parent} component={typedComponent} context={context} componentPath={componentPath} />;
    case "InputField":
      return <InputFieldComponent key={key} parent={parent} component={typedComponent} context={context} componentPath={componentPath} />;
    case "InputFile":
      return <InputFileComponent key={key} parent={parent} component={typedComponent} context={context} componentPath={componentPath} />;
    case "InputSelect":
      return <InputSelectComponent key={key} parent={parent} component={typedComponent} context={context} componentPath={componentPath} />;
    case "InputSlider":
      return <InputSliderComponent key={key} parent={parent} component={typedComponent} context={context} componentPath={componentPath} />;
    case "Panel":
      return <PanelComponent key={key} parent={parent} component={typedComponent} context={context} componentPath={componentPath} />;
    case "PopoverContainer":
      return <PopoverContainerComponent key={key} parent={parent} component={typedComponent} context={context} componentPath={componentPath} />;
    case "Chip":
      return <ChipComponent key={key} parent={parent} component={typedComponent} context={context} componentPath={componentPath} />;
    case "RepeatContainer":
      return <RepeatContainerComponent key={key} parent={parent} component={typedComponent} context={context} componentPath={componentPath} />;
    case "StackContainer":
      return <StackContainerComponent key={key} parent={parent} component={typedComponent} context={context} componentPath={componentPath} />;
    case "Timeline":
      return <TimelineComponent key={key} parent={parent} component={typedComponent} context={context} componentPath={componentPath} />;
    case "Tabs":
      return <TabsComponent key={key} parent={parent} component={typedComponent} context={context} componentPath={componentPath} />;
    case "ValueCounter":
      return <ValueCounterComponent key={key} parent={parent} component={typedComponent} context={context} componentPath={componentPath} />;
    case "PieChart":
      return <PieChartComponent key={key} parent={parent} component={typedComponent} context={context} componentPath={componentPath} />;
    case "BarChart":
      return <BarChartComponent key={key} parent={parent} component={typedComponent} context={context} componentPath={componentPath} />;
    case "ViewBreadcrumbs":
      return <ViewBreadcrumbsComponent key={key} parent={parent} component={typedComponent} context={context} componentPath={componentPath} />;
    case "WebView":
      return <WebViewComponent key={key} parent={parent} component={typedComponent} context={context} componentPath={componentPath} />;
    case "View":
      // ViewComponent has a special signature for dialog views, doesn't use standard IComponentProps
      return <ViewComponent key={component.Id} component={typedComponent} />;
    case "Reference":
      return <ReferenceComponent key={key} parent={parent} component={typedComponent} context={context} componentPath={componentPath} />;
    case "ReactComponentHost":
      return <ReactComponentHost key={key} parent={parent} component={typedComponent} context={context} componentPath={componentPath} />;
    default:
      logger.warn(`Unknown component type: ${component.Type}`);
      return <UnknownComponent key={key} parent={parent} component={typedComponent} context={context} componentPath={componentPath} />;
  }
};

const SuspendedComponentFactory: React.FC<ComponentFactoryProps> = (props) => (
  <React.Suspense fallback={<div role="status">Loading�</div>}><ComponentFactory {...props} /></React.Suspense>
);
export default React.memo(SuspendedComponentFactory);
